/**
 * Suspicious v2 — one action-row shape for every detail panel
 * (Suspicious sender, Person, Bot / Agent / scanner, MandateDetailV3).
 *
 * Layout: a single row at the top-right of the panel header.
 *   Primary: Block for 7 days ▾ (duration menu)
 *   Secondary: ⋯ (More actions) → popover menu with:
 *     Trust this address             — "Sill won't alert on or block this address."
 *     Ignore for 30 days  (threat)   — "Sill won't alert on these attempts for 30 days."
 *     Hold signed requests (agents)  — "Flags the agent as needing review. Signed
 *                                       requests still go through today; a future
 *                                       release will pause them for manual approval."
 *     Refuse signed requests (agents) — "Signed requests from this agent are rejected
 *                                        at the edge. Payments and other signed
 *                                        actions don't go through. Page visits are
 *                                        unaffected."
 *     Report to network (bots)       — "Flags this bot to other Sill merchants, so
 *                                        a bot many sites have reported stands out
 *                                        in everyone's dashboard."
 *     Share link                     — "Copy a link that reopens this panel."
 *
 * Verified crawler: no Block button (verified line sits where the row would
 * be). The ⋯ menu still shows Share + Report when applicable.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, MoreHorizontal } from 'lucide-react';

import { useC } from '../../lib/theme.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';

const sansFont = "'Geist', sans-serif";

/**
 * Hover / focus feedback for every button in the row (founder
 * 2026-10-19: "the buttons to block should have some feedback when you
 * hover"). Inline styles can't express :hover, and an inline background
 * would beat a class background anyway, so the hover is an inset shade
 * that reads on lime, on transparent, and in both themes.
 */
const ACTION_ROW_CSS = `
.sill-action-btn { transition: box-shadow 120ms ease, filter 120ms ease; }
.sill-action-btn:not(:disabled):hover { box-shadow: inset 0 0 0 999px rgba(127,127,127,0.16); }
.sill-action-btn:not(:disabled):active { box-shadow: inset 0 0 0 999px rgba(127,127,127,0.28); }
.sill-action-btn:focus-visible { outline: 2px solid currentColor; outline-offset: 1px; }
`;

/** How long the "Link copied" confirmation stays up. */
export const SHARE_COPIED_MS = 2000;

export type ActionDuration = '24h' | '7d' | 'forever';

const DURATION_LABEL: Record<ActionDuration, string> = {
  '24h': 'Block for 24 hours',
  '7d': 'Block for 7 days',
  forever: 'Block forever',
};
const DURATION_MENU: Record<ActionDuration, string> = {
  '24h': '24 hours',
  '7d': '7 days (recommended)',
  forever: 'Forever',
};

export interface DetailActionRowProps {
  /** Hide the Block button entirely (verified crawler, viewer / reviewer). */
  readonly blockMode?: 'direct' | 'addresses' | 'none';
  /**
   * Called when the primary Block fires with a chosen duration.
   * `direct` surfaces (sender / person): the caller runs the POST.
   * `addresses` surfaces (bot / agent / scanner): the caller opens
   *   the address-selection flow (BotAddressBlockSection's Block-all
   *   confirm, for example).
   */
  readonly onBlock?: (duration: ActionDuration) => void | Promise<void>;
  /** Pending indicator on the Block button. */
  readonly blockPending?: boolean;
  /**
   * Founder 2026-10-27 (engin8): when this install cannot block
   * addresses (WordPress plugin only, no Worker), Block renders greyed
   * and inert with this sentence as its tooltip instead of a live
   * button that fails on click.
   */
  readonly blockDisabledReason?: string | null;

  /**
   * Founder bug 2026-10-18 (block-state consistency): when the
   * address(es) this panel covers are already blocked, the primary
   * switches from Block ▾ to a "Blocked until <date>" chip + an
   * Unblock button. `blockedUntilLabel` is the full human sentence
   * ("Blocked until Oct 18", "Blocked forever", "Block saved · not
   * active"). `onUnblock` runs the DELETE flow; omit to render the
   * chip read-only. "Change duration" is added to the ⋯ menu when
   * `onChangeBlockDuration` is passed.
   */
  readonly blockedUntilLabel?: string;
  readonly blockedTone?: 'coral' | 'amber';
  readonly onUnblock?: () => void | Promise<void>;
  readonly unblockPending?: boolean;
  readonly onChangeBlockDuration?: (duration: ActionDuration) => void | Promise<void>;

  /** Menu items — passing `undefined` for a handler hides that item. */
  readonly onTrust?: () => void | Promise<void>;
  readonly onIgnore?: () => void | Promise<void>;
  readonly onHold?: () => void | Promise<void>;
  readonly onRefuse?: () => void | Promise<void>;
  readonly onClearOverride?: () => void | Promise<void>;
  readonly currentOverride?: 'hold' | 'refuse' | null;
  readonly onReport?: () => void | Promise<void>;
  readonly onShare?: () => void | Promise<void>;
  readonly shareJustCopied?: boolean;
  /**
   * Where the Block ▾ and ⋯ menus open. 'up' for the mobile sticky bar at
   * the bottom of the panel, where a downward menu is off-screen
   * (founder 2026-10-18: "the block drop down is not working on mobile").
   */
  readonly menuPlacement?: 'down' | 'up';
}

interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly onClick: () => void | Promise<void>;
  readonly tone?: 'default' | 'coral';
}

export function DetailActionRow(props: DetailActionRowProps): JSX.Element {
  const C = useC();
  const blockMode = props.blockMode ?? 'direct';
  const [duration, setDuration] = useState<ActionDuration>('7d');
  const [durationMenuOpen, setDurationMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const closeMore = useCallback(() => setMoreOpen(false), []);
  // Share closes the menu, so a label change inside it was never seen
  // (founder 2026-10-19). Confirm next to the row instead.
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), SHARE_COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);
  const showCopied = copied || props.shareJustCopied === true;
  // Close menus on a tap/click outside the row. mouseleave never fires
  // reliably on touch screens, so it can't be the close signal.
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!durationMenuOpen && !moreOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (rowRef.current !== null && e.target instanceof Node && rowRef.current.contains(e.target)) return;
      setDurationMenuOpen(false);
      setMoreOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [durationMenuOpen, moreOpen]);
  const menuEdge = props.menuPlacement === 'up'
    ? { bottom: '100%', marginBottom: 4 }
    : { top: '100%', marginTop: 4 };

  const items: MenuItem[] = [];
  if (props.onTrust !== undefined) {
    items.push({
      id: 'trust',
      label: 'Trust this address',
      description: "Sill won't alert on or block this address.",
      onClick: () => { closeMore(); void props.onTrust!(); },
    });
  }
  if (props.onIgnore !== undefined) {
    items.push({
      id: 'ignore',
      label: 'Ignore for 30 days',
      description: "Sill won't alert on these attempts for 30 days.",
      onClick: () => { closeMore(); void props.onIgnore!(); },
    });
  }
  if (props.currentOverride === 'hold' || props.currentOverride === 'refuse') {
    if (props.onClearOverride !== undefined) {
      items.push({
        id: 'clear_override',
        label: props.currentOverride === 'refuse' ? 'Stop refusing signed requests' : 'Stop holding signed requests',
        description: 'Remove the override; signed requests from this agent are evaluated normally.',
        onClick: () => { closeMore(); void props.onClearOverride!(); },
      });
    }
  } else {
    if (props.onHold !== undefined) {
      items.push({
        id: 'hold',
        label: 'Hold signed requests',
        description: 'Flags the agent as needing review. Signed requests still go through today; a future release will pause them for manual approval.',
        onClick: () => { closeMore(); void props.onHold!(); },
      });
    }
    if (props.onRefuse !== undefined) {
      items.push({
        id: 'refuse',
        label: 'Refuse signed requests',
        description: "Signed requests from this agent are rejected at the edge. Payments and other signed actions don't go through. Page visits are unaffected.",
        tone: 'coral',
        onClick: () => { closeMore(); void props.onRefuse!(); },
      });
    }
  }
  if (props.onReport !== undefined) {
    items.push({
      id: 'report',
      label: 'Report to network',
      description: "Flags this bot to other Sill merchants. A bot many sites have reported stands out in everyone's dashboard.",
      onClick: () => { closeMore(); void props.onReport!(); },
    });
  }
  // Founder bug 2026-10-18: when the address(es) covered are already
  // blocked, offer "Change duration" and "Trust this address" (which
  // trusts + unblocks in one go — the server-side allow path).
  const alreadyBlocked = typeof props.blockedUntilLabel === 'string';
  if (alreadyBlocked && props.onChangeBlockDuration !== undefined) {
    items.push({
      id: 'change_duration',
      label: 'Change duration',
      description: 'Shift this block to 24 hours, 7 days, or forever.',
      onClick: () => { closeMore(); void props.onChangeBlockDuration!(duration); },
    });
  }
  if (props.onShare !== undefined) {
    items.push({
      id: 'share',
      label: 'Share link',
      description: 'Copy a link that reopens this panel.',
      onClick: () => {
        closeMore();
        void Promise.resolve(props.onShare!()).finally(() => setCopied(true));
      },
    });
  }

  const showBlock = blockMode !== 'none' && props.onBlock !== undefined && !alreadyBlocked;

  return (
    <div
      ref={rowRef}
      data-testid="detail-action-row"
      style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexShrink: 0 }}
    >
      <style>{ACTION_ROW_CSS}</style>
      {showCopied ? (
        <span
          role="status"
          aria-live="polite"
          data-testid="detail-action-share-copied"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.text,
            padding: '4px 8px',
            border: `1px solid ${C.border}`,
            background: C.surface2,
            borderRadius: 2,
            whiteSpace: 'nowrap',
          }}
        >
          Link copied
        </span>
      ) : null}
      {alreadyBlocked ? (
        <div
          data-testid="detail-action-blocked-chip"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              padding: '4px 8px',
              border: `1px solid ${(props.blockedTone ?? 'coral') === 'amber' ? C.amberFg : C.coralFg}`,
              color: (props.blockedTone ?? 'coral') === 'amber' ? C.amberFg : C.coralFg,
              borderRadius: 2,
            }}
          >
            {props.blockedUntilLabel}
          </span>
          {props.onUnblock !== undefined ? (
            <button
              type="button"
              className="sill-action-btn"
              data-testid="detail-action-unblock"
              onClick={() => void props.onUnblock!()}
              disabled={props.unblockPending === true}
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                padding: '5px 10px',
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                cursor: props.unblockPending === true ? 'wait' : 'pointer',
                opacity: props.unblockPending === true ? 0.6 : 1,
              }}
            >
              {props.unblockPending === true ? 'Unblocking…' : 'Unblock'}
            </button>
          ) : null}
        </div>
      ) : showBlock && typeof props.blockDisabledReason === 'string' ? (
        <Tooltip content={props.blockDisabledReason} side="bottom" data-testid="detail-action-block-disabled-tip">
          <span
            data-testid="detail-action-block-disabled"
            aria-disabled="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: sansFont,
              fontSize: 13,
              background: C.surface2,
              color: C.muted,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 10px',
              cursor: 'not-allowed',
              whiteSpace: 'nowrap',
            }}
          >
            Block
          </span>
        </Tooltip>
      ) : showBlock ? (
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <button
            type="button"
            className="sill-action-btn"
            data-testid="detail-action-block-primary"
            onClick={() => void props.onBlock!(duration)}
            disabled={props.blockPending === true}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRight: `1px solid ${C.ink}66`,
              borderRadius: '2px 0 0 2px',
              padding: '6px 10px',
              cursor: props.blockPending === true ? 'wait' : 'pointer',
              opacity: props.blockPending === true ? 0.6 : 1,
            }}
          >
            {DURATION_LABEL[duration]}
          </button>
          <button
            type="button"
            className="sill-action-btn"
            data-testid="detail-action-block-menu"
            aria-haspopup="menu"
            aria-expanded={durationMenuOpen}
            onClick={() => setDurationMenuOpen((o) => !o)}
            disabled={props.blockPending === true}
            style={{
              fontFamily: sansFont,
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRadius: '0 2px 2px 0',
              padding: '6px 8px',
              display: 'inline-flex',
              alignItems: 'center',
              cursor: props.blockPending === true ? 'wait' : 'pointer',
              opacity: props.blockPending === true ? 0.6 : 1,
            }}
          >
            <ChevronDown size={14} />
          </button>
          {durationMenuOpen ? (
            <div
              role="menu"
              data-testid="detail-action-block-menu-items"
              style={{
                position: 'absolute', ...menuEdge, right: 0,
                minWidth: 220,
                maxWidth: 'calc(100vw - 32px)',
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                zIndex: 10,
              }}
            >
              {(['24h', '7d', 'forever'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  role="menuitem"
                  className="sill-action-btn"
                  data-duration={d}
                  onClick={() => { setDuration(d); setDurationMenuOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: 'transparent', border: 'none',
                    padding: '7px 10px',
                    fontFamily: sansFont, fontSize: 13,
                    color: duration === d ? C.text : C.body,
                    cursor: 'pointer',
                  }}
                >
                  {DURATION_MENU[d]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {items.length > 0 ? (
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <button
            type="button"
            className="sill-action-btn"
            data-testid="detail-action-more"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label="More actions"
            onClick={() => setMoreOpen((o) => !o)}
            style={{
              fontFamily: sansFont,
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              padding: '6px 8px',
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <MoreHorizontal size={16} />
          </button>
          {moreOpen ? (
            <div
              role="menu"
              data-testid="detail-action-more-menu"
              style={{
                position: 'absolute', ...menuEdge, right: 0,
                minWidth: 'min(320px, calc(100vw - 32px))',
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                zIndex: 11,
                boxShadow: `0 2px 8px ${C.border}22`,
              }}
            >
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  role="menuitem"
                  className="sill-action-btn"
                  data-testid={`detail-action-more-${it.id}`}
                  onClick={it.onClick}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: 'transparent', border: 'none',
                    padding: '10px 12px',
                    fontFamily: sansFont,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  <div style={{
                    fontSize: 13,
                    color: it.tone === 'coral' ? C.coralFg : C.text,
                    fontWeight: 500,
                  }}>
                    {it.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                    {it.description}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
